import { useCallback, useMemo, useRef, useState } from "react";
import {
    AccountingMigrationsColumn,
    AccountingMigrationsView,
    AccountingMigrationsViewSchema,
} from "@common/types/views/accountingMigrationsView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { MigrationObjectType } from "@common/types/domains/accounting/accountingMigration";
import {
    useRefreshAccountingMigrationMutation,
    useRemoveAccountingMigrationMutation,
    useWorkspaceAccountingMigrations,
} from "@api/accountingIntegration.api";
import { useEntities } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { Button } from "@design-system/Button/Button";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { Section } from "@design-system/Section/Section";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useOverlays } from "contexts/OverlayContext";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getMigrationDeleteToastMessage } from "@api/migrationDeleteFeedback";
import { PlusIcon } from "@icons";
import {
    AccountingMigrationsTableMeta,
    AccountingMigrationsTableCallbacks,
} from "./Cells/ActionsCell";
import { createAccountingMigrationsViewConfig } from "./accountingMigrationsViewConfig";
import { ChangeMigrationDatesModal } from "./ChangeMigrationDatesModal";
import { RefreshMigrationModal } from "./RefreshMigrationModal";
import { buildAccountingMigrationRows } from "./utils";
import styles from "./AccountingMigrationsSection.module.scss";

const defaultView: AccountingMigrationsView = {
    type: ViewType.ACCOUNTING_MIGRATIONS,
    columns: [
        { id: AccountingMigrationsColumn.ENTITY },
        { id: AccountingMigrationsColumn.SOURCE },
        { id: AccountingMigrationsColumn.START_DATE },
        { id: AccountingMigrationsColumn.END_DATE },
        { id: AccountingMigrationsColumn.STATUS },
        { id: AccountingMigrationsColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.LG },
};

const viewConfig = createAccountingMigrationsViewConfig();

const confirmationMessage =
    "Removing the migration will delete its opening balances, migration tasks, and imported journal entries for this entity. This cannot be undone. Continue?";

const refreshConfirmationMessage =
    "Refreshing will re-import journal entries from QuickBooks Online. Any changes you made to the migrated data will be discarded. Continue?";

function withMigrationDateColumns(
    view: AccountingMigrationsView,
): AccountingMigrationsView {
    const columnIds = new Set(view.columns.map((column) => column.id));
    const missing: AccountingMigrationsView["columns"] = [];
    if (!columnIds.has(AccountingMigrationsColumn.SOURCE)) {
        missing.push({ id: AccountingMigrationsColumn.SOURCE });
    }
    if (!columnIds.has(AccountingMigrationsColumn.START_DATE)) {
        missing.push({ id: AccountingMigrationsColumn.START_DATE });
    }
    if (!columnIds.has(AccountingMigrationsColumn.END_DATE)) {
        missing.push({ id: AccountingMigrationsColumn.END_DATE });
    }
    if (missing.length === 0) {
        return view;
    }

    const sourceIndex = view.columns.findIndex(
        (column) => column.id === AccountingMigrationsColumn.SOURCE,
    );
    const entityIndex = view.columns.findIndex(
        (column) => column.id === AccountingMigrationsColumn.ENTITY,
    );
    let insertAt = 0;
    if (sourceIndex >= 0) {
        insertAt = sourceIndex + 1;
    } else if (entityIndex >= 0) {
        insertAt = entityIndex + 1;
    }

    return {
        ...view,
        columns: [
            ...view.columns.slice(0, insertAt),
            ...missing,
            ...view.columns.slice(insertAt),
        ],
    };
}

interface Props {
    workspaceId: string;
}

export const AccountingMigrationsSection: React.FC<Props> = ({
    workspaceId,
}) => {
    const { openOverlay } = useOverlays();
    const { quickbooksMigration, qboRefreshChoices } = useTypedFlags();
    const { data: accountingMigrations = [] } =
        useWorkspaceAccountingMigrations(workspaceId);
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const rows = useMemo(
        () => buildAccountingMigrationRows(entities, accountingMigrations),
        [accountingMigrations, entities],
    );

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<AccountingMigrationsView>(
            WORKSPACE_USER_CACHE_KEYS.ACCOUNTING_MIGRATIONS_VIEW_CACHE_KEY,
            AccountingMigrationsViewSchema,
        );
    const [view, setView] = useState<AccountingMigrationsView>(
        cachedView ? withMigrationDateColumns(cachedView) : defaultView,
    );
    const [entityIdPendingRemoval, setEntityIdPendingRemoval] = useState<
        number | null
    >(null);
    const [entityIdPendingRefresh, setEntityIdPendingRefresh] = useState<
        number | null
    >(null);
    const [entityIdPendingEndDate, setEntityIdPendingEndDate] = useState<
        number | null
    >(null);
    const { error: errorToast } = useToaster();
    const removeAccountingMigration = useRemoveAccountingMigrationMutation(
        workspaceId,
        0,
    );
    const refreshAccountingMigration =
        useRefreshAccountingMigrationMutation(workspaceId);

    const handleViewChange = useCallback(
        (newView: View) => {
            const accountingMigrationsView =
                AccountingMigrationsViewSchema.parse(newView);
            setView(accountingMigrationsView);
            setCachedView(accountingMigrationsView);
        },
        [setCachedView],
    );

    const removeMigration = useCallback<
        AccountingMigrationsTableCallbacks["removeMigration"]
    >(async ({ entityId }) => {
        setEntityIdPendingRemoval(entityId);
    }, []);

    const handleConfirmRemoval = useCallback(async () => {
        if (entityIdPendingRemoval == null) {
            return;
        }

        try {
            await removeAccountingMigration.mutateAsync({
                entityId: entityIdPendingRemoval,
            });
            setEntityIdPendingRemoval(null);
        } catch (error) {
            errorToast(
                getMigrationDeleteToastMessage(error, {
                    statusesWithServerMessage: [409],
                }),
            );
        }
    }, [entityIdPendingRemoval, errorToast, removeAccountingMigration]);

    const handleCloseConfirmationModal = useCallback(
        () => setEntityIdPendingRemoval(null),
        [],
    );

    const refreshMigration = useCallback<
        AccountingMigrationsTableCallbacks["refreshMigration"]
    >(async ({ entityId }) => {
        setEntityIdPendingRefresh(entityId);
    }, []);

    const handleConfirmRefresh = useCallback(
        async (objectTypes?: MigrationObjectType[]) => {
            if (entityIdPendingRefresh == null) {
                return;
            }

            await refreshAccountingMigration.mutateAsync({
                entityId: entityIdPendingRefresh,
                objectTypes,
            });
            setEntityIdPendingRefresh(null);
        },
        [entityIdPendingRefresh, refreshAccountingMigration],
    );

    const handleCloseRefreshModal = useCallback(
        () => setEntityIdPendingRefresh(null),
        [],
    );
    const pendingRefreshRow = rows.find(
        (row) => row.entity.id === entityIdPendingRefresh,
    );
    const lastRefreshRowRef = useRef(pendingRefreshRow);
    if (pendingRefreshRow) {
        lastRefreshRowRef.current = pendingRefreshRow;
    }
    const refreshModalRow = pendingRefreshRow ?? lastRefreshRowRef.current;

    const changeMigrationDates = useCallback<
        AccountingMigrationsTableCallbacks["changeMigrationDates"]
    >(({ entityId }) => {
        setEntityIdPendingEndDate(entityId);
    }, []);

    const handleCloseEndDateModal = useCallback(
        () => setEntityIdPendingEndDate(null),
        [],
    );
    const pendingEndDateRow = rows.find(
        (row) => row.entity.id === entityIdPendingEndDate,
    );
    const lastEndDateRowRef = useRef(pendingEndDateRow);
    if (pendingEndDateRow) {
        lastEndDateRowRef.current = pendingEndDateRow;
    }
    const endDateModalRow = pendingEndDateRow ?? lastEndDateRowRef.current;

    const tableMeta: AccountingMigrationsTableMeta = useMemo(
        () => ({
            callbacks: {
                removeMigration,
                refreshMigration,
                changeMigrationDates,
            },
        }),
        [removeMigration, refreshMigration, changeMigrationDates],
    );

    const handleNewMigration = useCallback(() => {
        openOverlay("accountingMigration", true);
    }, [openOverlay]);

    const heading = (
        <>
            <h4 className={styles.title}>Migration</h4>
            {quickbooksMigration && (
                <Button
                    variant="primary"
                    size="md"
                    leftIcon={<PlusIcon />}
                    onClick={handleNewMigration}
                >
                    New migration
                </Button>
            )}
        </>
    );

    return (
        <Section heading={heading}>
            {rows.length === 0 ? (
                <p>No active accounting migrations.</p>
            ) : (
                <ViewTable
                    data={rows}
                    view={view}
                    config={viewConfig}
                    onViewChange={handleViewChange}
                    tableMeta={tableMeta}
                    rowBorders={false}
                    outerBorder
                />
            )}
            <ConfirmationModal
                show={entityIdPendingRemoval != null}
                onHide={handleCloseConfirmationModal}
                onReject={handleCloseConfirmationModal}
                title="Remove accounting migration?"
                question={confirmationMessage}
                yes="Remove migration"
                yesBtnVariant="destructive"
                no="Cancel"
                onConfirm={handleConfirmRemoval}
            />
            {qboRefreshChoices ? (
                refreshModalRow && (
                    <RefreshMigrationModal
                        key={refreshModalRow.accountingMigration.id}
                        show={entityIdPendingRefresh != null}
                        accountingMigration={
                            refreshModalRow.accountingMigration
                        }
                        isSubmitting={refreshAccountingMigration.isPending}
                        onClose={handleCloseRefreshModal}
                        onConfirm={handleConfirmRefresh}
                    />
                )
            ) : (
                <ConfirmationModal
                    show={entityIdPendingRefresh != null}
                    onHide={handleCloseRefreshModal}
                    onReject={handleCloseRefreshModal}
                    title="Refresh accounting migration?"
                    question={refreshConfirmationMessage}
                    yes="Refresh migration"
                    yesBtnVariant="destructive"
                    no="Cancel"
                    onConfirm={() => handleConfirmRefresh()}
                />
            )}
            {endDateModalRow && (
                <ChangeMigrationDatesModal
                    key={endDateModalRow.accountingMigration.id}
                    show={entityIdPendingEndDate != null}
                    onHide={handleCloseEndDateModal}
                    workspaceId={workspaceId}
                    entity={endDateModalRow.entity}
                    accountingMigration={endDateModalRow.accountingMigration}
                />
            )}
        </Section>
    );
};
